import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import itPortalService from "../services/itPortalService";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { MdOutlineAccountBalanceWallet, MdOutlineAdminPanelSettings, MdOutlineReceiptLong, MdSave } from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ITPortalDashboard() {
  const [configs, setConfigs] = useState({
    login_max_attempts: 5,
    login_lockout_duration_minutes: 15,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const fetchConfigs = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await itPortalService.getConfigs();
      if (response.data && response.data.configs) {
        const configData = response.data.configs;
        const mappedConfigs = {};
        configData.forEach((item) => {
          mappedConfigs[item.config_key] = item.config_value;
        });
        setConfigs({
          login_max_attempts: mappedConfigs.login_max_attempts || 5,
          login_lockout_duration_minutes: mappedConfigs.login_lockout_duration_minutes || 15,
        });
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        setError("Failed to load security configurations.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError("");

    try {
      await itPortalService.updateConfigs({
        login_max_attempts: parseInt(configs.login_max_attempts, 10),
        login_lockout_duration_minutes: parseInt(configs.login_lockout_duration_minutes, 10),
      });
      showSnack("Security configurations updated successfully!");
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        const msg = err.response?.data?.message || "Failed to update configurations.";
        setError(msg);
        showSnack(msg, "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    setConfigs({
      ...configs,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        {/* Header Bar matching SuperAdmin style */}
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">IT Portal Security Settings</h2>
          </div>

          {/* IT Portal Subnavigation Tabs */}
          <div className="it-tabs-container">
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/user-topcoin")}
            >
              <MdOutlineAccountBalanceWallet size={18} /> User TopCoin Management
            </button>
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/vat-config")}
            >
              <MdOutlineReceiptLong size={18} /> VAT Configuration
            </button>
            <button
              className="it-tab-item active"
              onClick={() => navigate("/it-portal/dashboard?tab=security")}
            >
              <MdOutlineAdminPanelSettings size={18} /> Security Settings
            </button>
          </div>
        </div>

        {/* Security Settings Card */}
        <div className="it-card-container">
          <h3 className="it-card-title">Authentication & Brute Force Protection</h3>
          <p className="it-card-desc">
            Define system-wide rate limiting parameters, failed attempt thresholds, and lockout durations.
          </p>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
              Loading security configurations...
            </div>
          ) : (
            <form onSubmit={handleSave}>
              {error && (
                <div
                  style={{
                    color: "#dc2626",
                    marginBottom: "20px",
                    padding: "12px 16px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              <div className="it-form-field">
                <label>Maximum Failed Login Attempts</label>
                <input
                  type="number"
                  name="login_max_attempts"
                  min="1"
                  max="20"
                  value={configs.login_max_attempts}
                  onChange={handleChange}
                  required
                />
                <small>
                  The number of sequential invalid password attempts allowed before an account is locked out.
                </small>
              </div>

              <div className="it-form-field">
                <label>Account Lockout Duration (Minutes)</label>
                <input
                  type="number"
                  name="login_lockout_duration_minutes"
                  min="1"
                  max="1440"
                  value={configs.login_lockout_duration_minutes}
                  onChange={handleChange}
                  required
                />
                <small>
                  The time in minutes a user must wait before attempting to sign in again after hitting the threshold.
                </small>
              </div>

              <div style={{ marginTop: "30px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  className="action-btn"
                  disabled={isSaving}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  <MdSave size={18} />
                  {isSaving ? "Saving..." : "Save Settings"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
